import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { api } from '../services/api';
import {
  NEIGHBOR_SCAN_STARTED_EVENT, describeSiteAtStart, openNeighborScanTracker, scanOpenCount,
} from '../utils/neighborScanTracking';

const RECENT_COUNT = 5;
const POLL_MS = 10000;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const STATUS = {
  active: { label: 'In progress', color: 'warning' },
  done: { label: 'Finished', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'default' },
};

/**
 * The last few multi-device neighbors scans a user started, with their
 * status and per-state counts (no values); click one for its tracker.
 * Refreshes while any of them is in progress and when a scan starts.
 */
export default function RecentNeighborScans({ highlightScanId = null }) {
  const [scans, setScans] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      setError('');
      setScans((await api.listNeighborScans(RECENT_COUNT)).slice(0, RECENT_COUNT));
    } catch (err) {
      setError(err?.message || 'Failed to load recent scans');
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onStarted = () => { load(); };
    window.addEventListener(NEIGHBOR_SCAN_STARTED_EVENT, onStarted);
    return () => window.removeEventListener(NEIGHBOR_SCAN_STARTED_EVENT, onStarted);
  }, [load]);
  const anyActive = (scans || []).some((s) => s.status === 'active');
  useEffect(() => {
    if (!anyActive) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [anyActive, load]);

  return (
    <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" gutterBottom>Recent Background Scans</Typography>
          <Typography variant="body2" color="text.secondary">
            The last {RECENT_COUNT} multi-device scans users started, with their status. Click one to track each device.
            A scan you start from Run Scan appears here as it runs.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>
      {error && <Typography variant="body2" color="error" sx={{ px: 1.5, pb: 1 }}>{error}</Typography>}
      {!scans && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>}
      {scans && scans.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, pb: 1.5 }}>No multi-device scans in the last 30 days.</Typography>
      )}
      {scans && scans.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Started</TableCell>
                <TableCell>Devices</TableCell>
                <TableCell>Site when started</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Results</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {scans.map((s) => {
                const meta = STATUS[s.status] || STATUS.done;
                const open = scanOpenCount(s.summary);
                return (
                  <TableRow
                    key={s.id}
                    hover
                    selected={s.id === highlightScanId}
                    onClick={() => openNeighborScanTracker(s.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2">{fmt(s.created_at)}</Typography>
                      {s.created_by && <Typography variant="caption" color="text.secondary">{s.created_by}</Typography>}
                    </TableCell>
                    <TableCell>
                      {s.summary?.targeted ?? 0}{s.source === 'all' ? ' (whole site)' : ''}
                      {s.batch_size > 0 ? ` · ${s.batch_size} at a time` : ''}
                    </TableCell>
                    <TableCell>{describeSiteAtStart(s) || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={meta.label} color={meta.color} />
                      {s.status === 'active' && open > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">{open} to go</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" color="success" variant="outlined" label={`${s.summary?.received ?? 0} got results`} />
                        {s.summary?.overdue > 0 && <Chip size="small" color="error" variant="outlined" label={`${s.summary.overdue} no results`} />}
                        {s.summary?.not_sent > 0 && <Chip size="small" variant="outlined" label={`${s.summary.not_sent} not sent`} />}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
